import React, { useRef, useState } from 'react';

// Fallback only; the server serves response_window_ms on every block. Read
// through Number() because the runner derives its reaction-time ceiling as
// `timing().windowMs + 250`: a window arriving as a string would concatenate
// ("60000" + 250 -> "60000250") and every runaway value would be accepted as a
// real response. `||` rather than `??` so a served 0 falls back to the default
// instead of becoming an instant timeout that records the whole block as
// non-responses.
const DEFAULT_WINDOW_MS = 60000;

// These are stimulus data, not theme tokens: the balls are told apart by
// colour, so the colours must be literal and identical in dark and light mode.
// Deliberately exempt from the CSS-variable rule, as stroop's ink table is.
// Green is the dual-theme legible value, kept in sync with stroop/speedMatch:
// pure #30A46C is only 2.82:1 on the light --bg-primary (#F7F6F3).
const BALL_FILL = { red: '#E5484D', green: '#238F5C', blue: '#3E63DD' };

const capacitiesOf = (stimulus) =>
  Array.isArray(stimulus?.pegCapacities) ? stimulus.pegCapacities : [];

const startOf = (stimulus) => (Array.isArray(stimulus?.start) ? stimulus.start : []);

const goalOf = (stimulus) => (Array.isArray(stimulus?.goal) ? stimulus.goal : []);

// Null when the move is illegal: same peg, empty source, or a full destination.
// This is the APPARATUS, not the answer — in the physical task a ball under
// another ball cannot be lifted and a peg cannot hold a fourth ball. It says
// nothing about which moves lead to the goal, and no solution is computed
// anywhere in this file. The server re-checks both rules and an illegal move
// invalidates the WHOLE sequence, so refusing one here costs the participant
// nothing and accepting one costs them the trial.
function applyMove(state, capacities, from, to) {
  if (from === to) return null;
  if (!state[from] || state[from].length === 0) return null;
  if (!state[to] || state[to].length >= capacities[to]) return null;
  const ball = state[from][state[from].length - 1]; // only the TOP ball moves
  return state.map((stack, peg) => {
    if (peg === from) return stack.slice(0, -1);
    if (peg === to) return [...stack, ball];
    return stack;
  });
}

// One peg row. Interactive pegs are real buttons; the goal display is a
// non-interactive figure rather than a row of permanently disabled buttons,
// which would put three dead controls in the tab order's a11y tree for no
// reason.
//
// A peg's accessible name is its CONTENTS and its capacity, both of which a
// sighted participant can see. It must never describe how the peg relates to
// the goal — "peg 1 already matches" or "blue is home" would hand a
// screen-reader user a planning cue nobody else is given, and the score would
// still look entirely normal.
function Pegs({ state, capacities, label, namePrefix, onPegClick, selected, disabled }) {
  const maxCap = Math.max(1, ...capacities);
  return (
    <div role="group" aria-label={label} style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-end' }}>
      {capacities.map((capacity, peg) => {
        const stack = state[peg] || [];
        const name = `${namePrefix} ${peg + 1}, holds up to ${capacity}, ${
          stack.length === 0 ? 'empty' : `bottom to top ${stack.join(' then ')}`
        }`;
        const lifted = selected === peg;
        const style = {
          display: 'flex',
          flexDirection: 'column-reverse',
          justifyContent: 'flex-start',
          gap: 'var(--space-1)',
          padding: 'var(--space-2)',
          width: 52,
          height: 52 * maxCap,
          borderRadius: 'var(--radius-md)',
          fontFamily: 'inherit',
          border: lifted ? '2px solid var(--accent-primary)' : '1px solid var(--border-strong)',
          background: 'var(--bg-elevated)',
          boxSizing: 'border-box',
        };
        const balls = stack.map((ball, i) => (
          <span
            key={i}
            aria-hidden="true"
            style={{
              width: 38,
              height: 38,
              borderRadius: '50%',
              // An unrecognised colour still renders a visible ball rather than
              // inheriting whatever the runner chrome happens to paint.
              background: BALL_FILL[ball] || 'var(--text-primary)',
            }}
          />
        ));

        return onPegClick ? (
          <button
            key={peg}
            type="button"
            disabled={Boolean(disabled)}
            onClick={() => onPegClick(peg)}
            aria-label={name}
            aria-pressed={lifted}
            style={{ ...style, cursor: disabled ? 'default' : 'pointer' }}
          >
            {balls}
          </button>
        ) : (
          <div key={peg} role="img" aria-label={name} style={style}>
            {balls}
          </div>
        );
      })}
    </div>
  );
}

const captionStyle = {
  fontSize: 'var(--text-label-size)',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--text-tertiary)',
};

// Tap a source peg, then a destination. Deliberately NOT drag-only: a tap-tap
// interaction is reliable on touch and with a keyboard, and drag adds nothing
// the measurement uses.
//
// THERE IS NO UNDO AND NO RESTART. The budget is exactly the minimum number of
// moves, and any slack lets a participant wander into the goal without planning
// — this paradigm's version of an item answerable without performing its
// cognitive operation. A control that walks a move back makes the budget
// meaningless and the resulting score looks entirely normal.
//
// Nothing here computes or checks the solution: no solved-state detector, no
// comparison of the board against the goal, no optimal-move count, no hint. The
// goal is drawn beside the board and that is the whole of what the participant
// is told.
function StimulusView({ stimulus, disabled, onFirstInteraction, onCommit }) {
  const capacities = capacitiesOf(stimulus);
  // `|| 0` so a stimulus with no usable minMoves accepts no moves at all and
  // submits as an omission, rather than a bare `>=` firing on the first move.
  const budget = Number(stimulus?.minMoves) || 0;

  const [shown, setShown] = useState(stimulus);
  const [board, setBoard] = useState(() => startOf(stimulus));
  const [moves, setMoves] = useState([]);
  const [from, setFrom] = useState(null);
  // Guards a click landing after the answer has gone. The runner ignores a
  // second commit, but a late click would still redraw the board as though it
  // had counted.
  const committed = useRef(false);

  // A new problem must begin from ITS OWN start configuration. RunnerShell
  // renders this component at a fixed position in its tree, so React would
  // reconcile rather than remount if two stimulus stages ever met without an
  // intervening fixation or gap, and the next problem would inherit the
  // previous one's finished board. Comparing the stimulus identity during
  // render is React's documented way to derive state from props.
  const arrived = shown !== stimulus;
  if (arrived) {
    setShown(stimulus);
    setBoard(startOf(stimulus));
    setMoves([]);
    setFrom(null);
    committed.current = false;
  }
  const live = arrived ? startOf(stimulus) : board;
  const made = arrived ? [] : moves;
  const holding = arrived ? null : from;

  const commit = (sequence) => {
    committed.current = true;
    onCommit({ moves: sequence });
  };

  const pick = (peg) => {
    if (disabled || committed.current || made.length >= budget) return;
    if (holding === null) {
      if (!live[peg] || live[peg].length === 0) return; // nothing to lift
      setFrom(peg);
      return;
    }
    if (holding === peg) {
      setFrom(null); // put it back down; not a move
      return;
    }
    const next = applyMove(live, capacities, holding, peg);
    if (!next) {
      setFrom(null); // illegal; nothing is recorded
      return;
    }
    // Stamps the planning latency: problem appearing to the FIRST move, never
    // total solution time. Idempotent in the runner, so later moves do not move
    // it. It must happen BEFORE any commit — commitComposite refuses an answer
    // with no first interaction behind it, and the trial would silently submit
    // as an omission.
    onFirstInteraction();
    // Appended, never sorted, deduped or reordered: the order of the moves and
    // any peg revisited ARE the measurement.
    const nextMoves = [...made, [holding, peg]];
    setBoard(next);
    setMoves(nextMoves);
    setFrom(null);
    // The budget is exactly the minimum, and the server rejects a longer
    // sequence outright, so there is nothing to gain from accepting more.
    if (nextMoves.length >= budget) commit(nextMoves);
  };

  const left = Math.max(0, budget - made.length);

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', justifyItems: 'center' }}>
      <div style={{ display: 'flex', gap: 'var(--space-7)', alignItems: 'flex-end' }}>
        <div style={{ display: 'grid', gap: 'var(--space-2)', justifyItems: 'center' }}>
          <span style={captionStyle}>Goal</span>
          <Pegs
            state={goalOf(stimulus)}
            capacities={capacities}
            label="Goal arrangement"
            namePrefix="Goal peg"
          />
        </div>
        <div style={{ display: 'grid', gap: 'var(--space-2)', justifyItems: 'center' }}>
          <span style={captionStyle}>Yours</span>
          <Pegs
            state={live}
            capacities={capacities}
            label="Your arrangement"
            namePrefix="Peg"
            onPegClick={pick}
            selected={holding}
            disabled={disabled}
          />
        </div>
      </div>
      {/* The budget is a CONSTRAINT the participant is told, exactly as in the
          published task. It is not the solution, and it is never compared
          against anything the client works out for itself. */}
      <span
        aria-live="polite"
        style={{ fontSize: 'var(--text-body-size)', fontWeight: 700, color: 'var(--text-secondary)' }}
      >
        {left} move{left === 1 ? '' : 's'} left
      </span>
      <button
        type="button"
        onClick={() => {
          // A partial sequence is a WRONG ANSWER, not missing data, and it is
          // sent. A trial with no moves at all is the one case that must NOT
          // commit: it submits as an all-null omission, which is what the runner
          // produces when onFirstInteraction is never called.
          if (disabled || committed.current || made.length === 0) return;
          commit(made);
        }}
        disabled={disabled || made.length === 0}
        style={{
          padding: 'var(--space-3) var(--space-5)',
          borderRadius: 'var(--radius-md)',
          fontWeight: 700,
          fontSize: 'var(--text-panel-size)',
          border: '1px solid var(--border-strong)',
          background: 'var(--bg-elevated)',
          color: 'var(--text-primary)',
          fontFamily: 'inherit',
          cursor: made.length === 0 ? 'default' : 'pointer',
        }}
      >
        Give up
      </button>
    </div>
  );
}

export const towerOfLondonTask = {
  id: 'tower-of-london',
  name: 'Three Pegs',
  pillar: 'problem',

  // Composite: the trial ends at commitComposite regardless, so the driver's
  // early-advance timer never applies here. Declared anyway — registry.test.js
  // requires an explicit pacing declaration from every module.
  holdsFullWindow: false,

  instructions: [
    'Move the balls to match the goal on the left.',
    'Tap a peg to lift its top ball, then tap where it goes.',
    'You get exactly the number of moves shown — plan before you start, every move is final.',
  ],

  // The board IS the response surface, so there are no button targets.
  responseMode: 'composite',
  targets: [],

  // Empty deliberately. A worked example row would have to print a start and a
  // goal beside the move sequence that solves them, which is the one thing this
  // instrument measures. TaskExamples renders null for an empty list.
  examples: [],

  // Never reached: a composite task builds its own answer object.
  toAnswer: () => ({}),

  // A planning measure, not a speed one, so the window is generous on purpose:
  // deliberating for forty-five seconds is the intended behaviour here, not an
  // anomaly. The runner derives its reaction-time ceiling as windowMs + 250 and
  // the server derives its own the same way (ai-learn-api 867a885), so a first
  // move anywhere inside this window is accepted by both. This value is
  // therefore load-bearing: a task that returned no window would fall back to
  // the runner's 30000ms default and silently record every careful participant
  // as a non-response.
  //
  // fixationMs and gapMs are fixed rather than subtracted from a served
  // interval: no isi_ms is served for this instrument. Do not harmonise with
  // flanker, stroop or pattern-comparison, whose conventions are mandated
  // per-instrument.
  timing: (params) => ({
    fixationMs: 400,
    windowMs: Number(params?.response_window_ms) || DEFAULT_WINDOW_MS,
    gapMs: 600,
  }),

  // Solving requires a breadth-first search the client has no business running,
  // and the optimal path is not in the payload. Nothing on the client can say
  // whether a sequence was right, so no feedback is ever available — not even on
  // a practice trial.
  feedbackAvailable: false,
  practiceExpectation: () => null,

  StimulusView,
};
